// src/components/shared/makeEnvCellRenderer.js
// Factory for the promote-to-env columns. Combines the two states a cell
// can be in:
//
//   1. Row doesn't exist in that env yet ("N/A")  -> ConfirmPromoteDialog
//      (push this record to that env) — unchanged from before.
//   2. Row exists in that env                     -> DiffIndicatorIcon
//      (grey/orange indicator; click to open the page's single SyncDialog).
//
// Row-level diff info is expected to have been precomputed and attached to
// each row under `${envColumnId}__diff` (see envUtils.getRowDiff), since
// the diff needs the full record from both envs, not just the single
// promote-eligibility field the cell displays.
//
// NOTE: unlike ConfirmPromoteDialog (which owns its own Modal per cell),
// the sync dialog itself is NOT rendered here — it must be rendered once,
// separately, at the page level (see confirmMatchDialog.js's SyncDialog).
// Mounting a <Modal> per cell for this flow blows up into 2xN
// simultaneously-"open" dialogs the moment any row's icon is clicked,
// which is unreliable to click in a real browser.
//
// Usage (inside a component that has these from useDataView):
//
//   const envCell1Renderer = makeEnvCellRenderer({
//     openPromoteModal, envCell, handleClickOpen, handleClose, handleConfirm,
//     handleMatchClickOpen,
//   });
//   // ...and separately, once per page:
//   <SyncDialog open={openMatchModal} matchCell={matchCell} ... />

import React from "react";
import ConfirmPromoteDialog from "../util/confirmPromoteDialog";
import DiffIndicatorIcon from "../util/diffIndicatorIcon";

export function makeEnvCellRenderer({
  openPromoteModal,
  envCell,
  handleClickOpen,
  handleClose,
  handleConfirm,
  handleMatchClickOpen,
}) {
  return function ({ cell }) {
    const cellValue = cell.getValue();

    if (cellValue === "N/A") {
      return (
        <ConfirmPromoteDialog
          cell={cell}
          open={openPromoteModal}
          envCell={envCell}
          handleClickOpen={handleClickOpen}
          handleClose={handleClose}
          handleConfirm={handleConfirm}
        />
      );
    }

    const diffInfo = cell.row.original[`${cell.column.id}__diff`] || {
      isDifferent: false,
      diffFields: [],
      targetRow: null,
    };

    return (
      <>
        {cellValue}
        <DiffIndicatorIcon
          cell={cell}
          isDifferent={diffInfo.isDifferent}
          diffFields={diffInfo.diffFields}
          targetRow={diffInfo.targetRow}
          handleMatchClickOpen={handleMatchClickOpen}
        />
      </>
    );
  };
}
